import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Col, Form, Divider } from 'antd';
import { useSelector } from 'react-redux';
import ResourceManagementOptions from './ResourceManagementOptions';
import FormTeleconsultation from './FormTeleconsultation';
import { useDictionaryTranslation } from '../../../hooks/useDictionaryTranslation';
import { CustomSwitch } from '../../SelfScheduling/components/form/editScheduleBlocks/CustomSwitch';
import { SelectHeadquarters } from '../../../components/headquarters/SelectHeadquarters';
import { SelectServiceType } from '../../../components/services/forms/SelectServiceType';
import { SelectUser } from '../../../components/users/SelectUser';
import { profileTypes } from '../../../config/constants/userConstants';
import { SelectSkills } from '../../../components/skills/SelectSkills';
import { theme } from '../../../config/theme/themeVariables';
import { TitleFormDefault } from '../../../container/styled';
import { SelectCompany } from '../../../components/services/forms/SelectCompany';

const FormOperator = ({
  form,
  userData,
  headquarters,
  viewAllServices,
  onChangeHeadquarters,
  onChangeViewAllServices,
  videoConsultationRoom,
  viewServicesCustomers,
}) => {
  const { translate: t } = useDictionaryTranslation();
  const { accountModules } = useSelector((state) => ({
    accountModules: state.auth.accountModules,
  }));
  const headquartersIds = useMemo(
    () => (headquarters?.length > 0 ? headquarters.map((x) => x.value) : [null]),
    [headquarters],
  );
  const adminAllUsers = Form.useWatch('adminAllUsers', form);

  return (
    <>
      <Divider />
      <Col xs={24} md={20}>
        <TitleFormDefault>{t('skills')}</TitleFormDefault>
        <span>{t('skillsDescription')}</span>
        <Link to="/settings/skills/">
          <span style={{ color: theme['primary-color'] }}>{t('skillsDescriptionLink')}</span>
        </Link>
        <span>{t('skillsDescriptionFinish')}</span>
        <Form.Item name="skills">
          <SelectSkills multiple />
        </Form.Item>
      </Col>
      <Divider />
      <Col xs={24} md={20}>
        <TitleFormDefault>{t('headquarter')}</TitleFormDefault>
        <h4>{t('headquarterDescription')}</h4>
        <Form.Item name="headquarters">
          <SelectHeadquarters multiple onChange={onChangeHeadquarters} />
        </Form.Item>
      </Col>
      <Divider />
      <Col xs={24} md={20}>
        <TitleFormDefault>{t('viewServicesCustomers')}</TitleFormDefault>
        <Form.Item
          name="isAllowedToViewAllCompanies"
          label={t('viewServicesCustomersDescription')}
          valuePropName="checked"
        >
          <CustomSwitch checkedText={t('yes')} unCheckedText={t('no')} />
        </Form.Item>
        {!viewServicesCustomers ? (
          <Form.Item label={t('selectCompanies')} name="assignedCompanies" valuePropName="selectedCompany">
            <SelectCompany multiple />
          </Form.Item>
        ) : null}
      </Col>
      <Divider />
      <Col xs={10} md={20}>
        <h4>
          <TitleFormDefault>{t('resourceManagement')}</TitleFormDefault>
        </h4>
        <Form.Item name="adminAllUsers" label={t('resourceManagement')} valuePropName="checked">
          <CustomSwitch checkedText={t('yes')} unCheckedText={t('no')} />
        </Form.Item>
      </Col>
      {!adminAllUsers ? (
        <Col xs={24} md={20}>
          <Form.Item label={t('resourceDescription')} name="resourcesEnabledToAdmin" valuePropName="selectedUser">
            <SelectUser
              multiple
              headquarters={headquartersIds}
              placeholder={t('resourcePlaceholder')}
              idProfile={profileTypes.TECHNICIAN.id}
            />
          </Form.Item>
        </Col>
      ) : null}
      <Divider />
      <Col xs={10} md={20}>
        <h4>
          <TitleFormDefault>{t('serviceAssignment')}</TitleFormDefault>
        </h4>
        <Form.Item name="administrateServices" label={t('administrateServices')} valuePropName="checked">
          <CustomSwitch checkedText={t('yes')} unCheckedText={t('no')} />
        </Form.Item>
        <Form.Item name="seeAllServices" label={t('seeAllServices')} valuePropName="checked">
          <CustomSwitch checkedText={t('yes')} unCheckedText={t('no')} onChange={onChangeViewAllServices} />
        </Form.Item>

        {!viewAllServices ? (
          <Form.Item
            name="servicesTypesAssigned"
            label={t('servicesTypesAssigned')}
            valuePropName="selectedServiceType"
          >
            <SelectServiceType multiple />
          </Form.Item>
        ) : null}
      </Col>

      <Col>
        <Form.Item name="isAllowedToViewAllAuthors" label={t('viewAllAuthorsServices')} valuePropName="checked">
          <CustomSwitch checkedText={t('yes')} unCheckedText={t('no')} />
        </Form.Item>
      </Col>
      <Col xs={10} md={20}>
        <h4 style={{ marginLeft: '10px', fontWeight: 'bold' }}>{t('allowAssignServicesTo')}</h4>

        <Form.Item
          name="canAssignServicesToTechnician"
          label={t('technician')}
          valuePropName="checked"
          style={{ marginBottom: '0px' }}
        >
          <CustomSwitch checkedText={t('yes')} unCheckedText={t('no')} />
        </Form.Item>
        <Form.Item name="canAssignServicesToOperator" label={t('operator')} valuePropName="checked">
          <CustomSwitch checkedText={t('yes')} unCheckedText={t('no')} />
        </Form.Item>
      </Col>
      <Divider />
      <ResourceManagementOptions form={form} />
      {userData?._id && accountModules.videoConsultationRoom && !videoConsultationRoom.isLoading ? (
        <Col xs={24} md={24}>
          <FormTeleconsultation
            idUser={userData._id}
            roomData={videoConsultationRoom.dataRoom}
            onChangeRoomData={videoConsultationRoom.updateRoomData}
          />
        </Col>
      ) : null}
    </>
  );
};

FormOperator.propTypes = {
  headquarters: PropTypes.array,
  viewAllServices: PropTypes.bool,
  onChangeHeadquarters: PropTypes.func,
  onChangeViewAllServices: PropTypes.func,
  videoConsultationRoom: PropTypes.object,
  userData: PropTypes.object,
  viewServicesCustomers: PropTypes.bool,
  form: PropTypes.object,
};

export default FormOperator;
